import { useParams } from 'react-router-dom'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  DialogProps,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import client from 'services/client'
import { BaseData } from 'types/base.type'
import BalanceIcon from '@mui/icons-material/Balance'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import DialogContent from '@mui/material/DialogContent'
import { DataGridCustom } from 'pages/CreateCreas/components/TableData/style'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'

export type AdolescenteIntegraJudicialType = {
  id?: number | null
  nomeAdolescente?: string | null
  habilitado?: boolean
}

type AdolescentesIntegraJudicialModalProps = {
  onAdd: (data: AdolescenteIntegraJudicialType[]) => Promise<void>
  onChangeDate: () => Promise<void>
}

export const AdolescentesIntegraJudicialModal = ({
  onAdd,
  onChangeDate,
}: AdolescentesIntegraJudicialModalProps) => {
  const { pendingProcessHearingId } = useParams()

  const [fullWidth] = useState(true)
  const [maxWidth] = useState<DialogProps['maxWidth']>('lg')

  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [isFetching, setFetching] = useState<boolean>(true)
  const [open, setOpen] = useState<boolean>(false)
  const [data, setData] = useState<AdolescenteIntegraJudicialType[]>([])
  const handleOpen = async () => {
    await onChangeDate().finally(() => setOpen(true))
  }

  const handleClose = () => {
    setOpen(false)
    setData([])
  }

  const columns = useMemo<GridColDef<AdolescenteIntegraJudicialType, any, any>[]>(
    () => [
      {
        field: 'nomeAdolescente',
        headerName: 'Adolescentes',
        flex: 1,
        sortable: false,
        disableReorder: true,
        disableExport: true,
        disableColumnMenu: true,
        renderCell: (params) => params.value,
        valueGetter: (params) => params.value,
      },
    ],
    [],
  )

  const handleAdd = async () => {
    const filteredData = data.filter((item) => item.id && selectedRows.includes(item.id))
    await onAdd(filteredData).finally(() => handleClose())
  }

  const buscar = async () => {
    const url = '/integra-policial/processos'
    setFetching(true)
    await client
      .post<BaseData<AdolescenteIntegraJudicialType[]>>(
        `${url}/${pendingProcessHearingId}/integra-judicial/adolescentes`,
      )
      .then((response) => {
        if (response && response.data) {
          setData(response.data)
        }
      })
      .finally(() => setFetching(false))
  }

  useEffect(() => {
    ;(async () => {
      if (open) {
        await buscar()
      }
    })()
  }, [open])

  return (
    <Fragment>
      <Tooltip title={'Integra Judicial'}>
        <IconButton onClick={handleOpen}>
          <BalanceIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <Typography variant={'h6'} color={'primary.main'}>
            Selecione os adolescentes disponíveis para Oitiva Informal
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {!isFetching ? (
            data.length > 0 ? (
              <DataGridCustom
                checkboxSelection
                autoHeight
                hideFooterPagination
                loading={isFetching}
                rows={data}
                columns={columns}
                localeText={{
                  noRowsLabel:
                    'Até o momento, não há registro de adolescentes informados no processo no Integra Judicial. Inclua manualmente clicando no botão',
                  footerRowSelected: (numero) =>
                    numero > 1 ? `${numero} selecionados.` : `${numero} selecionado.`,
                }}
                onRowSelectionModelChange={(newSelection: any) => {
                  setSelectedRows(newSelection)
                }}
                getRowId={(row) => row.id}
                rowSelectionModel={selectedRows}
                isRowSelectable={(params: GridRowParams) => params.row.habilitado}
              />
            ) : (
              <p>
                Até o momento, não há registro de adolescentes informados no processo no Integra
                Judicial. Inclua manualmente clicando no botão{' '}
                <Tooltip title='Adicionar adolescente.' placement={'top'}>
                  <PersonAddAltIcon fontSize='inherit' />
                </Tooltip>
                .
              </p>
            )
          ) : (
            <Box display={'flex'} justifyContent={'center'}>
              <CircularProgress />
            </Box>
          )}
          {data.length > 0 && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'end'}
              marginTop={'1rem'}
              gap={2}
            >
              <Button variant={'text'} color={'secondary'} size={'large'} onClick={handleClose}>
                Voltar
              </Button>
              <Button
                variant={'contained'}
                color={'secondary'}
                size={'large'}
                onClick={handleAdd}
                disabled={selectedRows.length === 0}
              >
                Adicionar
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  )
}
